import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Flex, Input, Space } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import useApiClient from '../../../hooks/api/useApiClient';
import { useGetServiceContractQuery } from '../../../store/api-slices';
import { actionCreators } from '../../../store/actions';
import YousignInPersonSigning from './YousignInPersonSigning';

const SIGNATURE_STATUS = Object.freeze({
  Done: 'done',
  Declined: 'declined',
});

/**
 * Renders the embedded Yousign signing iframe for an already-initiated signature
 * request. Once the signature is done the backend processes it asynchronously
 * (webhook sets `signed_at`), so we poll the contract and hand off the freshly
 * refetched contract on continue.
 */
export default function YousignSignFrame({ contractId, signatureLink, onSuccess, onDeclined, backButton }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Contract:SignStep' });

  const { ServiceContractsManager } = useApiClient();
  const dispatch = useDispatch();

  const [signatureStatus, setSignatureStatus] = useState(null);
  const [signedContract, setSignedContract] = useState(null);

  const isSigned = signatureStatus === SIGNATURE_STATUS.Done;
  const { data: refetchedContract } = useGetServiceContractQuery(
    { apiManager: ServiceContractsManager },
    { skip: !isSigned || Boolean(signedContract?.signed_at), pollingInterval: 5000, refetchOnMountOrArgChange: true },
  );

  useEffect(() => {
    if (refetchedContract?.signed_at) {
      setSignedContract(refetchedContract);
      dispatch(actionCreators.currentCompany.updateMerchant({ service_contract: refetchedContract }));
    }
  }, [refetchedContract]);

  return (
    <div className="mt-medium relative">
      {(signatureLink || backButton) && (
        <Flex justify="space-between" align="flex-end" gap="small" wrap className="mb-medium">
          {signatureLink ? (
            <div className="flex-1 min-w-[280px]">
              <p className="mb-2 font-medium">{t('signature_link.label')}</p>
              <Space.Compact className="w-full max-w-xl">
                <Input readOnly value={signatureLink} />
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(signatureLink);
                    toast.success(t('signature_link.copied'));
                  }}
                >
                  {t('signature_link.copy')}
                </Button>
              </Space.Compact>
            </div>
          ) : (
            <span />
          )}
          {!refetchedContract?.signed_at && backButton}
        </Flex>
      )}
      <YousignInPersonSigning
        contractId={contractId}
        signatureLink={signatureLink}
        onSignatureDone={() => setSignatureStatus(SIGNATURE_STATUS.Done)}
        onDeclined={() => setSignatureStatus(SIGNATURE_STATUS.Declined)}
      />
      {signatureStatus === SIGNATURE_STATUS.Done && (
        <Flex justify="end" gap="small" className="mt-4">
          <Button
            type="primary"
            loading={!signedContract?.signed_at}
            onClick={() => signedContract && onSuccess(signedContract)}
          >
            {t('buttons.continue')}
          </Button>
        </Flex>
      )}
      {signatureStatus === SIGNATURE_STATUS.Declined && (
        <Flex justify="end" gap="small" className="mt-4">
          <Button onClick={() => onDeclined && onDeclined()}>{t('buttons.back')}</Button>
        </Flex>
      )}
    </div>
  );
}
